<template>
  <div class="adspot">
    <Row>
      <iCol
        span="8"
      >
        <Button
          class="add-button"
          type="primary"
          icon="md-add"
          size="large"
          @click="add"
        >
          添加广告位
        </Button>
      </iCol>
      <iCol
        span="4"
        offset="12"
      >
        <iInput
          v-model="searchText"
          placeholder="输入广告名称"
          style="width: auto"
          @on-enter="search"
        >
          <Icon
            slot="suffix"
            type="ios-search"
            @click="search"
          />
        </iInput>
      </iCol>
    </Row>

    <Form
      ref="adspotForm"
      :model="editAdspot"
      :rules="rules"
    >
      <Table
        ref="adspotTable"
        :columns="columns"
        :data="pageAdspotList"
        size="large"
      >
        <template
          slot="name"
          slot-scope="{ row: { id, adspot_name } }"
        >
          <FormItem
            v-if="editId === id"
            class="form-item"
            prop="adspot_name"
          >
            <Input
              v-model="editAdspot.adspot_name"
              size="large"
              :autofocus="true"
            />
          </FormItem>
          <p
            v-else
            v-text="adspot_name"
          />
        </template>
        <template
          slot="description"
          slot-scope="{ row: { id, adspot_description } }"
        >
          <FormItem
            v-if="editId === id"
            class="form-item"
            prop="adspot_description"
          >
            <Input
              v-model="editAdspot.adspot_description"
              type="textarea"
              :autosize="true"
              size="large"
            />
          </FormItem>
          <p
            v-else
            v-text="adspot_description"
          />
        </template>
        <template
          slot="media"
          slot-scope="{ row: { id, media_id } }"
        >
          <FormItem
            v-if="editId === id"
            class="form-item"
            prop="media_id"
          >
            <Select
              v-model="editAdspot.media_id"
              size="large"
              :disabled="!!id"
              transfer
            >
              <Option
                v-for="(media, index) in mediaList"
                :key="index"
                :value="media.id"
                :label="media.media_name"
              />
            </Select>
          </FormItem>
          <p
            v-else
            v-text="getMediaName(media_id)"
          />
        </template>
        <template
          slot="status"
          slot-scope="{ row: { id, status } }"
        >
          <iSwitch
            v-if="editId === id"
            v-model="editAdspot.status"
            size="large"
            :true-value="1"
            :false-value="0"
          >
            <span slot="open">
              打开
            </span>
            <span slot="close">
              关闭
            </span>
          </iSwitch>
          <p
            v-else
            v-text="status ? '打开' : '关闭'"
          />
        </template>
        <template
          slot="edit"
          slot-scope="{ row, index }"
        >
          <div v-if="editId === row.id">
            <Button
              v-if="row.id"
              class="operation"
              type="primary"
              icon="md-checkmark"
              @click="submitEdit"
            />
            <Button
              v-else
              class="operation"
              type="primary"
              icon="md-checkmark"
              @click="submitAdd"
            />
            <Button
              v-if="row.id"
              class="operation"
              icon="md-close"
              @click="cancelEdit"
            />
            <Button
              v-else
              class="operation"
              icon="md-trash"
              @click="cancelAdd"
            />
            <Poptip
              confirm
              transfer
              title="确定要删除吗？"
              ok-text="删除"
              cancel-text="取消"
              @on-ok="deleteAdspot({ adspot: row })"
            >
              <Button
                v-if="row.id"
                class="operation delete"
                type="error"
                icon="md-trash"
              />
            </Poptip>
          </div>
          <div v-else>
            <Button
              class="operation"
              icon="md-create"
              @click="edit({ adspot: Object.assign({}, row) })"
            />
            <Tooltip
              placement="top"
              content="复制广告位及渠道配置"
              :transfer="true"
            >
              <Button
                class="operation"
                icon="md-copy"
                @click="showCopyModal(row.id)"
              />
            </Tooltip>
            <Tooltip
              placement="right"
              class="expand-button-tooltip"
              :content="$refs.adspotTable && $refs.adspotTable.objData[index]._isExpanded ? '关闭渠道设置' : '展开渠道设置'"
              :transfer="true"
            >
              <Button
                class="operation expandable"
                :class="{ expanded: $refs.adspotTable && $refs.adspotTable.objData[index]._isExpanded }"
                type="text"
                icon="ios-arrow-forward"
                @click="expand(index, row)"
              />
            </Tooltip>
          </div>
        </template>
      </Table>
      <template v-if="total > 10">
        <Page
          class-name="pager"
          :total="total"
          :current="current"
          @on-change="pageChanged"
        />
      </template>
    </Form>
    <Modal
      v-model="copyModalVisible"
      :loading="copyModalLoading"
      title="复制广告位"
      :width="420"
      :mask-closable="false"
      :closable="false"
    >
      <Form
        ref="copyAdspotModal"
        :model="newCopiedAdspot"
        :label-width="100"
        :rules="copyFormRules"
        @submit.native.prevent
      >
        <p class="copy-form-hint-text">
          将会复制出一个相同的新广告位，包含相同的渠道配置
        </p>
        <FormItem
          class="copy-form-form-item"
          label="新广告位名称"
          prop="adspot_name"
        >
          <iInput
            v-model="newCopiedAdspot.adspot_name"
          />
        </FormItem>
      </Form>
      <div slot="footer">
        <Button @click="closeCopyModal">
          取消
        </Button>
        <Button
          type="primary"
          @click="submitCopyForm"
        >
          确认
        </Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import {
  Table,
  Button,
  Input,
  Switch,
  Select,
  Option,
  Form,
  FormItem,
  Tooltip,
  Poptip,
  Page,
  Icon,
  Row,
  Col,
  Modal
} from 'iview'
import AdspotSupplier from '../components/AdspotSupplier'
import axios from 'axios'

export default {
  components: {
    Table,
    Button,
    Input,
    iSwitch: Switch,
    Select,
    Option,
    Form,
    FormItem,
    Tooltip,
    Poptip,
    Page,
    Icon,
    Row,
    iCol: Col,
    iInput: Input,
    Modal
  },

  data () {
    return {
      columns: [
        {
          type: 'expand',
          width: 40,
          render: (create, data) => create(
            AdspotSupplier,
            {
              props: {
                adspot: data.row,
                userName: this.userName
              }
            }
          )
        },
        { title: '广告位ID', key: 'id', width: 95 },
        { title: '广告位名', slot: 'name' },
        { title: '广告位描述', slot: 'description' },
        { title: '媒体名', slot: 'media' },
        { title: '状态', slot: 'status' },
        { title: '操作', slot: 'edit', width: 170 }
      ],

      rules: {
        adspot_name: [
          { required: true, message: '请填写广告位名称' }
        ],
        adspot_description: [
          { required: true, message: '请填写广告位描述' }
        ],
        media_id: [
          { required: true, message: '请选择媒体' }
        ]
      },
      copyFormRules: {
        adspot_name: [
          { required: true, message: '请填写广告位名称' }
        ]
      },
      searchText: '',
      copyModalVisible: false,
      copyModalLoading: true,
      newCopiedAdspot: {}
    }
  },

  computed: {
    ...mapState('adspot', [
      'pageAdspotList',
      'total',
      'current',
      'editId',
      'editAdspot'
    ]),

    ...mapState('media', [
      'mediaList'
    ]),

    ...mapState({
      userName: 'userName'
    })
  },

  created () {
    this.getList({ current: this.current })
    if (!this.mediaList || !this.mediaList.length) {
      this.getAllMedia()
    }
  },

  methods: {
    ...mapMutations('adspot', [
      'add',
      'cancelAdd',
      'edit',
      'cancelEdit'
    ]),

    ...mapActions('adspot', {
      getList: 'getList',
      deleteAdspot: 'delete',
      submitAdd (dispatch) {
        this.$refs.adspotForm.validate(isValid => {
          if (isValid) {
            dispatch('submitAdd')
          } else {
            this.$Message.error('填写信息有误！')
          }
        })
      },

      submitEdit (dispatch) {
        this.$refs.adspotForm.validate(isValid => {
          if (isValid) {
            dispatch('submitEdit')
          } else {
            this.$Message.error('填写信息有误！')
          }
        })
      },

      search () {
        this.getList({ current: this.current, searchText: this.searchText })
      }
    }),

    ...mapActions('media', {
      getAllMedia: 'getAll'
    }),

    getMediaName (id) {
      if (!this.mediaList || !this.mediaList.length) {
        return id
      }
      const media = this.mediaList.find(item => item.id === id)
      if (!media) {
        return id
      }
      return media.media_name
    },

    pageChanged (page) {
      this.getList({ current: page, searchText: this.searchText })
    },

    expand (index) {
      var tableRow = this.$refs.adspotTable.objData[index]
      tableRow._isExpanded = !tableRow._isExpanded
    },

    showCopyModal (adspotId) {
      this.copyModalVisible = true
      var adspotToCopy = this.pageAdspotList.find(item => item.id === adspotId)
      this.newCopiedAdspot = {
        ...adspotToCopy,
        adspot_name: adspotToCopy.adspot_name + ' - 复制'
      }
    },

    closeCopyModal () {
      this.copyModalVisible = false
    },

    submitCopyForm () {
      this.copyModalLoading = true
      this.$refs.copyAdspotModal.validate(isValid => {
        if (!isValid) {
          this.copyModalLoading = false
          return
        }

        const { adspot_name: adspotName } = this.newCopiedAdspot

        axios.post(`/adspot/${this.newCopiedAdspot.id}/copy`, {
          adspot: {
            adspot_name: adspotName
          }
        }).then(() => {
          this.searchText = undefined
          this.copyModalVisible = false
          this.getList({ current: 1 })
        }).finally(() => {
          this.copyModalLoading = false
        })
      })
    }
  }
}
</script>

<style lang="less" scoped>
.add-button {
  margin-bottom: 10px;
}
button.operation {
  margin-right: 5px;
  padding: 0;
  width: 33px;
  height: 33px;
  font-size: 16px;

  &:last-child {
    margin-right: 0;
  }

  &.delete {
    margin-left: 15px;
  }

  &.expandable {
    transition: transform .2s ease-in-out;

    &.expanded {
      transform: rotate(90deg)
    }
  }
}
.form-item {
  margin-top: 24px;
}
.pager {
  margin-top: 16px;
  text-align: center;
}
.expand-button-tooltip {
  margin: 3px 0 3px 20px;
}
.copy-form-form-item {
  margin-bottom: 6px;
}
.copy-form-hint-text {
  margin-bottom: 15px;
  color: #9ea7b4;
}
</style>
